import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useGuardedFunction from 'Hooks/useGuardedFunction';

const ScenarioChartContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const CHART_REFRESH_STARTED = 'chart-started';
const CHART_REFRESH_SUCCEEDED = 'chart-succeeded';
const CHART_REFRESH_FAILED = 'chart-refresh-failed';

const init = () => {
  return {
    chartCollection: null,
    chartRefreshError: null,
    initializingError: null,
    isChartRefreshing: false,
    isInitializing: true,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        chartCollection: action.chartCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case CHART_REFRESH_STARTED: {
      return {
        ...state,
        isChartRefreshing: true,
        chartRefreshError: null,
      };
    }
    case CHART_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isChartRefreshing: false,
        chartRefreshError: null,
        chartCollection: action.chartCollection,
      };
    }
    case CHART_REFRESH_FAILED: {
      return {
        ...state,
        isChartRefreshing: false,
        chartRefreshError: action.chartRefreshError,
      };
    }
    default: {
      throw new Error(
        `Unhandled ScenarioChartContext action type: ${action.type}`,
      );
    }
  }
};

const ScenarioChartProvider = ({
  broadcastTimeframe,
  chartDataTimeframe,
  children,
  query,
  relationship,
  resource,
  setChartDataTimeframe,
  onLoadSucceeded,
  onLoadFailed,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const url = new URL(resource.relationship(relationship).link('related').attr('url'));
  const [chartDataChartId, setChartDataChartId] = React.useState(url.searchParams.get('chartId'));
  const [usePresentData, setChartDataUsePresentData] = React.useState(Boolean(url.searchParams.get('usePresentData') === 'true'));
  const {
    guardFunction, setGuardPassing,
  } = useGuardedFunction(false);

  const _refreshChartCollection = React.useCallback(
    async () => {
      dispatch({
        type: CHART_REFRESH_STARTED,
      });

      try {
        const refreshedCollection = await resource
          .relationship(relationship)
          .link('related')
          .get({
            params: {
              chartId: chartDataChartId,
              timeFrame: chartDataTimeframe,
              usePresentData,
            },
          });

        dispatch({
          type: CHART_REFRESH_SUCCEEDED,
          chartCollection: refreshedCollection,
        });
        if (onLoadSucceeded) {
          onLoadSucceeded();
        }
      } catch (err) {
        dispatch({
          type: CHART_REFRESH_FAILED,
          chartRefreshError: err,
        });
        if (onLoadFailed) {
          onLoadFailed(err);
        }
      }
    },
    [chartDataChartId, relationship, state, chartDataTimeframe, usePresentData],
  );
  const refreshChartCollection = guardFunction(_refreshChartCollection);

  const loadChartCollection = React.useCallback(
    async () => {
      dispatch({
        type: INITIALIZE_STARTED,
      });

      try {
        const chartCollection = await resource
          .relationship(relationship)
          .link('related')
          .get({
            params: {
              chartId: chartDataChartId,
              timeFrame: chartDataTimeframe,
              usePresentData,
            },
          });

        dispatch({ type: INITIALIZE_SUCCEEDED, chartCollection });
        if (onLoadSucceeded) {
          onLoadSucceeded();
        }
      } catch (err) {
        dispatch({ type: INITIALIZE_FAILED, initializationError: err });
        if (onLoadFailed) {
          onLoadFailed(err);
        }
      }

      setGuardPassing();
    },
    [chartDataChartId, relationship, state, chartDataTimeframe, usePresentData],
  );

  React.useEffect(() => {
    refreshChartCollection();
  }, [chartDataTimeframe, chartDataChartId, resource?.instanceId(), relationship, usePresentData]);

  useComponentDidMount(() => {
    loadChartCollection();
  });

  const memoizedValue = React.useMemo(() => {
    return {
      broadcastTimeframe,
      chartDataChartId,
      chartDataResource: state.chartCollection?.resources().head(),
      chartDataTimeframe,
      chartRefreshError: state.chartRefreshError,
      initializingError: state.initializingError,
      isChartRefreshing: state.isChartRefreshing,
      isInitializing: state.isInitializing,
      query,
      setChartDataChartId,
      setChartDataTimeframe,
      setChartDataUsePresentData,
      usePresentData,
    };
  }, [
    broadcastTimeframe,
    chartDataChartId,
    chartDataTimeframe,
    query,
    setChartDataChartId,
    setChartDataTimeframe,
    setChartDataUsePresentData,
    state,
    usePresentData,
  ]);

  return (
    <ScenarioChartContext.Provider value={memoizedValue}>
      {children}
    </ScenarioChartContext.Provider>
  );
};

// Use Template State Context
const useScenarioChartContext = () => {
  const context = React.useContext(ScenarioChartContext);
  if (context === undefined) {
    throw new Error(
      'useScenarioChartContext must be used within a ScenarioChartProvider',
    );
  }
  return context;
};

ScenarioChartProvider.defaultProps = {
  broadcastTimeframe: undefined,
  chartDataTimeframe: 'max',
  setChartDataTimeframe: undefined,
  onLoadSucceeded: () => {},
  onLoadFailed: () => {},
};

ScenarioChartProvider.propTypes = {
  broadcastTimeframe: PropTypes.func,
  chartDataTimeframe: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  query: PropTypes.string.isRequired,
  relationship: PropTypes.string.isRequired,
  resource: PropTypes.object.isRequired,
  setChartDataTimeframe: PropTypes.func,
  onLoadSucceeded: PropTypes.func,
  onLoadFailed: PropTypes.func,
};

export { ScenarioChartProvider, useScenarioChartContext };
